import React from "react";
import { useSelector } from "react-redux";

import { LAUNCH_ASIN_TYPES } from "../../../constants/launchAsinTypes";
import { RootState } from "../../app/rootReducer";
import type { PivotRow } from "./stepBreakdown";

export interface PivotRowLabelProps {
  readonly row: PivotRow;
}

const LABEL_STYLE: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "#000000",
  fontSize: "1.5rem",
  fontWeight: 700,
};

const GRAND_TOTAL_LABEL_STYLE: React.CSSProperties = {
  ...LABEL_STYLE,
  fontWeight: 800,
};

const COUNT_STYLE: React.CSSProperties = {
  color: "var(--color-text-body-secondary, #5f6b7a)",
  fontWeight: 400,
  marginLeft: "0.25rem",
};

/**
 * Row-label cell for the step-breakdown pivot. Renders the row's
 * display label followed by a subtle `(count)` suffix. For
 * productLine and program rows, resolves the raw ID stored on
 * `row.path`'s last token into a human-readable name via the
 * Slice 1 lookup slices — the connector attempts the same lookup
 * at fetch time, but the id-to-name fetches may still be in flight
 * when the pivot response lands.
 */
export const PivotRowLabel: React.FC<PivotRowLabelProps> = ({ row }) => {
  const productLines = useSelector(
    (state: RootState) => state.productLines.productLinesData.values
  );
  const programs = useSelector(
    (state: RootState) => state.upstreamAttributesData.programNameData.values
  );

  const displayLabel = React.useMemo(() => {
    const rawId =
      row.path.length > 0 ? row.path[row.path.length - 1].value : row.label;
    if (row.level === "productLine") {
      const match = productLines.find(
        (pl) => typeof pl?.id === "string" && pl.id === rawId
      );
      if (match?.name) {
        return match.name;
      }
    } else if (row.level === "program") {
      const match = programs.find(
        (p) => typeof p?.id === "string" && p.id === rawId
      );
      if (match?.name) {
        return match.name;
      }
    } else if (row.level === "asinType") {
      // Shared with the FilterBar chip so a pivot row and its
      // corresponding chip (e.g. "ASIN type = Fresh Lot") show the
      // same user-facing text. Unknown raw values fall through to
      // `row.label` unchanged.
      const friendly = LAUNCH_ASIN_TYPES[rawId];
      if (friendly !== undefined) {
        return friendly;
      }
    }
    return row.label;
  }, [row.level, row.label, row.path, productLines, programs]);

  const isGrandTotal = row.level === "grandTotal";
  return (
    <span style={isGrandTotal ? GRAND_TOTAL_LABEL_STYLE : LABEL_STYLE}>
      {displayLabel}
      <span style={COUNT_STYLE}>({row.totalCount})</span>
    </span>
  );
};

export default PivotRowLabel;
